import { NavLink, useNavigate } from "react-router-dom";
import { useAuth } from "../context/AuthContext";
import {
  LayoutDashboard,
  ArrowLeftRight,
  Scale,
  Upload,
  Shield,
  ShieldCheck,
  LogOut,
  ChevronDown,
  Check,
  Search,
  Users,
  Moon,
  Sun,
  ExternalLink,
} from "lucide-react";
import { useEffect, useState } from "react";
import GlobalSearch from "./GlobalSearch";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "./ui/dropdown-menu";

const BASE_NAV = [
  { to: "/", label: "Overview", icon: LayoutDashboard },
  { to: "/accounts", label: "Accounts", icon: Users },
  { to: "/entries", label: "Entries", icon: ArrowLeftRight },
  { to: "/balance-sheet", label: "Balance Sheet", icon: Scale },
  { to: "/backfill", label: "Backfill", icon: Upload },
  { to: "/permissions", label: "Permissions", icon: Shield },
];

export default function AppShell({ children }) {
  const { user, logout, workspaces, activeWorkspace, switchWorkspace, mode, switchMode } = useAuth();
  const navigate = useNavigate();
  const [menuOpen, setMenuOpen] = useState(false);
  const [searchOpen, setSearchOpen] = useState(false);
  const [theme, setTheme] = useState(() => localStorage.getItem("theme") || "dark");

  // Apply theme via <html data-theme="..."> so CSS variables cascade globally.
  useEffect(() => {
    document.documentElement.setAttribute("data-theme", theme);
    localStorage.setItem("theme", theme);
  }, [theme]);

  const toggleTheme = () => setTheme((t) => (t === "dark" ? "light" : "dark"));

  // Global keyboard shortcuts: Cmd/Ctrl+K for search, Alt+D also for search,
  // and Alt+E / Alt+B / Alt+O for the top nav pills (Entries / Balance Sheet /
  // Overview) so keyboard-driven users don't have to reach for the mouse.
  useEffect(() => {
    const h = (e) => {
      // ⌘/Ctrl + K OR Alt + D → global search. Use `code` for Alt+letter
      // because macOS Option+letter yields a special char in `e.key`.
      const cmdK = (e.metaKey || e.ctrlKey) && !e.altKey && !e.shiftKey && e.key.toLowerCase() === "k";
      const altD = e.altKey && !e.metaKey && !e.ctrlKey && !e.shiftKey && e.code === "KeyD";
      if (cmdK || altD) {
        e.preventDefault();
        setSearchOpen(true);
        return;
      }
      // Nav pills — only fire when the user isn't typing into an input, and
      // when the Alt (macOS Option) modifier is held on its own.
      if (!e.altKey || e.metaKey || e.ctrlKey || e.shiftKey) return;
      const tag = (e.target?.tagName || "").toLowerCase();
      if (tag === "input" || tag === "textarea" || e.target?.isContentEditable) return;
      const routeByCode = {
        KeyO: "/",
        KeyE: "/entries",
        KeyB: "/balance-sheet",
      };
      const to = routeByCode[e.code];
      if (to) {
        e.preventDefault();
        navigate(to);
      }
    };
    window.addEventListener("keydown", h);
    return () => window.removeEventListener("keydown", h);
  }, []);

  const NAV = user?.role === "admin"
    ? [...BASE_NAV, { to: "/admin", label: "Admin", icon: ShieldCheck }]
    : BASE_NAV;

  const initial = (user?.name || user?.email || "?").charAt(0).toUpperCase();

  return (
    <div className="min-h-screen relative">
      {/* Top bar */}
      <header className="sticky top-0 z-30 pt-6 px-6 lg:px-10">
        <div className="max-w-[1600px] mx-auto glass rounded-full flex items-center justify-between gap-3 px-3 py-2">
          {/* Brand */}
          <button
            onClick={() => navigate("/")}
            className="flex items-center gap-3 pl-2 pr-4 py-1 rounded-full hover:bg-white/40 transition-colors"
            data-testid="brand-home"
          >
            <img src="/logo.svg" alt="Solartoner" className="w-9 h-9 rounded-2xl" />
            <div className="text-left leading-tight hidden sm:block">
              <div className="font-serif text-lg leading-none">Solartoner</div>
              <div className="label-tiny mt-0.5">Private workspace</div>
            </div>
          </button>

          {/* Nav pills */}
          <nav className="hidden md:flex items-center gap-1 flex-1 justify-center">
            {NAV.map(({ to, label, icon: Icon }) => (
              <NavLink
                key={to}
                to={to}
                end={to === "/"}
                data-testid={`nav-${label.toLowerCase().replace(/\s+/g, "-")}`}
                className={({ isActive }) =>
                  `flex items-center gap-2 px-4 py-2 rounded-full text-sm font-medium transition-colors ${
                    isActive
                      ? "bg-[color:var(--gold)] text-white shadow-[0_4px_14px_rgba(184,147,90,0.35)]"
                      : "text-stone-700 hover:bg-white/60"
                  }`
                }
              >
                <Icon size={16} strokeWidth={1.5} />
                {label}
              </NavLink>
            ))}
          </nav>

          {/* Right: mode + workspace + user */}
          <div className="flex items-center gap-2">
            {/* Global search — styled as a dark text input so it reads
                clearly as "a search box", not another action pill. */}
            <button
              onClick={() => setSearchOpen(true)}
              className="global-search-trigger"
              data-testid="global-search-btn"
              title="Search across all entries (⌘K)"
            >
              <Search size={13} className="opacity-70 flex-shrink-0" />
              <span className="hidden sm:inline flex-1 text-left">Search…</span>
              <span className="hidden md:inline text-[10px] rounded px-1.5 py-0.5 font-mono border border-current opacity-70">
                ⌘K
              </span>
            </button>
            {/* Theme toggle (preview) */}
            <button
              onClick={toggleTheme}
              className="btn-ghost flex items-center gap-2 text-xs"
              title={theme === "dark" ? "Switch to light theme" : "Preview dark theme"}
              data-testid="theme-toggle-btn"
            >
              {theme === "dark" ? <Sun size={14} /> : <Moon size={14} />}
              <span className="hidden md:inline">{theme === "dark" ? "Light" : "Dark"}</span>
            </button>
            {/* Live/Demo pill */}
            <div
              className="flex items-center gap-0.5 bg-white/70 rounded-full p-0.5 border border-white/90"
              data-testid="mode-toggle"
            >
              <button
                onClick={() => switchMode("live")}
                className={`px-3 py-1.5 rounded-full text-xs font-medium transition-colors flex items-center gap-1.5 ${
                  mode === "live"
                    ? "bg-emerald-500 text-white shadow-[0_2px_8px_rgba(34,197,94,0.35)]"
                    : "text-stone-600"
                }`}
                data-testid="mode-live-btn"
              >
                {mode === "live" && <span className="w-1.5 h-1.5 rounded-full bg-white" />}
                Live
              </button>
              <button
                onClick={() => switchMode("demo")}
                className={`px-3 py-1.5 rounded-full text-xs font-medium transition-colors ${
                  mode === "demo"
                    ? "bg-[color:var(--gold)] text-white shadow-[0_2px_8px_rgba(184,147,90,0.35)]"
                    : "text-stone-600"
                }`}
                data-testid="mode-demo-btn"
              >
                Demo
              </button>
            </div>
            {workspaces && activeWorkspace && mode === "live" && (workspaces.shared?.length > 0 || activeWorkspace.owner_id !== workspaces.own?.owner_id) && (
              <DropdownMenu>
                <DropdownMenuTrigger
                  className="btn-ghost flex items-center gap-2 text-sm"
                  data-testid="workspace-switcher"
                >
                  <span className="live-dot" />
                  <span className="hidden sm:inline max-w-[140px] truncate">
                    {activeWorkspace.name}
                  </span>
                  <ChevronDown size={14} strokeWidth={1.5} />
                </DropdownMenuTrigger>
                <DropdownMenuContent className="glass-strong rounded-2xl min-w-[280px]" align="end">
                  <DropdownMenuLabel className="label-tiny">Workspaces</DropdownMenuLabel>
                  <div className="px-1 pb-1 text-[10px] text-stone-500">
                    Click a name to switch this tab. Click ↗ to open in a new tab (perfect for side-by-side).
                  </div>
                  <div className="flex items-stretch">
                    <DropdownMenuItem
                      className="flex-1"
                      onClick={() => switchWorkspace(workspaces.own)}
                      data-testid="workspace-own"
                    >
                      <div className="flex items-center justify-between w-full">
                        <div>
                          <div className="font-medium">{workspaces.own.name}</div>
                          <div className="text-xs text-stone-500">Owner</div>
                        </div>
                        {activeWorkspace.owner_id === workspaces.own.owner_id && (
                          <Check size={14} className="text-[color:var(--gold-deep)]" />
                        )}
                      </div>
                    </DropdownMenuItem>
                    <a
                      href={`?ws=${workspaces.own.owner_id}`}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="flex items-center justify-center w-10 rounded-md text-stone-500 hover:text-[color:var(--gold-deep)] hover:bg-[color:var(--gold-soft)]"
                      title="Open own workspace in a new tab"
                      data-testid="workspace-own-newtab"
                      onClick={(e) => e.stopPropagation()}
                    >
                      <ExternalLink size={13} strokeWidth={1.8} />
                    </a>
                  </div>
                  {workspaces.shared?.length > 0 && (
                    <>
                      <DropdownMenuSeparator />
                      <DropdownMenuLabel className="label-tiny">
                        Shared with me
                      </DropdownMenuLabel>
                      {workspaces.shared.map((w) => (
                        <div key={w.owner_id} className="flex items-stretch">
                          <DropdownMenuItem
                            className="flex-1"
                            onClick={() => switchWorkspace(w)}
                            data-testid={`workspace-${w.owner_id}`}
                          >
                            <div className="flex items-center justify-between w-full">
                              <div>
                                <div className="font-medium">{w.name}</div>
                                <div className="text-xs text-stone-500 capitalize">{w.level}</div>
                              </div>
                              {activeWorkspace.owner_id === w.owner_id && (
                                <Check size={14} className="text-[color:var(--gold-deep)]" />
                              )}
                            </div>
                          </DropdownMenuItem>
                          <a
                            href={`?ws=${w.owner_id}`}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="flex items-center justify-center w-10 rounded-md text-stone-500 hover:text-[color:var(--gold-deep)] hover:bg-[color:var(--gold-soft)]"
                            title={`Open ${w.name} in a new tab`}
                            data-testid={`workspace-${w.owner_id}-newtab`}
                            onClick={(e) => e.stopPropagation()}
                          >
                            <ExternalLink size={13} strokeWidth={1.8} />
                          </a>
                        </div>
                      ))}
                    </>
                  )}
                </DropdownMenuContent>
              </DropdownMenu>
            )}

            <DropdownMenu open={menuOpen} onOpenChange={setMenuOpen}>
              <DropdownMenuTrigger
                className="w-10 h-10 rounded-full glass-gold flex items-center justify-center font-medium text-[color:var(--gold-deep)] hover:scale-105 transition-transform"
                data-testid="user-menu"
              >
                {initial}
              </DropdownMenuTrigger>
              <DropdownMenuContent className="glass-strong rounded-2xl min-w-[200px]" align="end">
                <DropdownMenuLabel>
                  <div className="text-sm font-medium">{user?.name}</div>
                  <div className="text-xs text-stone-500">{user?.email}</div>
                </DropdownMenuLabel>
                <DropdownMenuSeparator />
                <DropdownMenuItem
                  onClick={() => {
                    logout();
                    navigate("/login");
                  }}
                  data-testid="logout-btn"
                  className="text-red-600"
                >
                  <LogOut size={14} className="mr-2" />
                  Sign out
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          </div>
        </div>

        {/* Mobile nav */}
        <div className="md:hidden mt-3 max-w-[1600px] mx-auto glass rounded-full flex items-center gap-1 px-2 py-1 overflow-x-auto">
          {NAV.map(({ to, label, icon: Icon }) => (
            <NavLink
              key={to}
              to={to}
              end={to === "/"}
              data-testid={`nav-mobile-${label.toLowerCase().replace(/\s+/g, "-")}`}
              className={({ isActive }) =>
                `flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs font-medium whitespace-nowrap transition-colors ${
                  isActive
                    ? "bg-[color:var(--gold)] text-white"
                    : "text-stone-700"
                }`
              }
            >
              <Icon size={14} strokeWidth={1.5} />
              {label}
            </NavLink>
          ))}
        </div>
      </header>

      <main className="max-w-[1600px] mx-auto px-6 lg:px-10 pt-6 pb-24">
        {mode === "demo" && (
          <div
            className="mb-4 rounded-full px-4 py-1.5 flex items-center justify-between gap-3 text-xs"
            style={{
              background:
                "linear-gradient(135deg, rgba(255,246,227,0.85) 0%, rgba(201,168,118,0.25) 100%)",
              border: "1px solid rgba(201,168,118,0.5)",
            }}
            data-testid="demo-banner"
          >
            <div className="flex items-center gap-2">
              <span className="w-1.5 h-1.5 rounded-full bg-[color:var(--gold-deep)]" />
              <span className="text-[color:var(--gold-deep)] font-medium">
                Demo mode — data is isolated from Live
              </span>
            </div>
            <button
              onClick={() => switchMode("live")}
              className="text-[color:var(--gold-deep)] font-medium hover:underline"
              data-testid="demo-exit-btn"
            >
              Switch to Live
            </button>
          </div>
        )}
        {children}
      </main>
      <GlobalSearch open={searchOpen} onClose={() => setSearchOpen(false)} />
    </div>
  );
}
